import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useAddMealToMealPlanByDate } from '../hooks/addMealToMealPlanByDate';
import { useAuth } from '../context/AuthContext';
import CompleteRecipeCookingButton from '../components/CompleteRecipeCookingButton';
import { fetchWithCSRF } from '../utils/csrfUtils';
import supabase from '../supabaseClient';
import EditRecipeButton from '../components/EditRecipeButton';
import { useSupabaseRecipeImage } from '../hooks/getRecipeImage';
import { getTodayLocalDateString, getDateWithOffset } from '../utils/dateUtils';
import { useAddRecipeDislike, useDeleteRecipeDislike, useGetRecipeDislikeStatus } from '../hooks/dislikeRecipe';
import { getIconUrl, getIngredientImageUrl } from '../utils/imageUtils';

function RecipeSteps() {
    const { pgUser } = useAuth();
    const { recipeId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const mealId = sessionStorage.getItem('currentMealId');
    const [recipe, setRecipe] = useState(null);
    const [recipeName, setRecipeName] = useState('');
    const [steps, setSteps] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [servings, setServings] = useState(0);
    const [timing, setTiming] = useState({
        prepTime: 0,
        cookTime: 0,
        totalTime: 0
    });
    const [nutrition, setNutrition] = useState({
        calories: 0,
        protein: 0,
        carbohydrate: 0,
        fat: 0
    });

    // Add new state for modal
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [selectedPlannedCookedDate, setSelectedPlannedCookedDate] = useState('');
    const [mealType, setMealType] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [dateError, setDateError] = useState('');
    const [mealTypeError, setMealTypeError] = useState('');
    const [sessionToken, setSessionToken] = useState('');

    // Add the meal plan hook
    const { addMealToMealPlanByDate, isLoading, error } = useAddMealToMealPlanByDate();

    // Add dislike hooks
    const { addRecipeDislike } = useAddRecipeDislike();
    const { deleteRecipeDislike } = useDeleteRecipeDislike();
    const { getRecipeDislikeStatus } = useGetRecipeDislikeStatus();
    const [isDisliked, setIsDisliked] = useState(false);
    const [isTogglingDislike, setIsTogglingDislike] = useState(false);

    // Helper function to format quantity as clean fractions when possible
    const formatQuantity = (num) => {
        const allowedFractions = [
            { fraction: 1/16, label: "1/16", vulgar: "¹⁄₁₆" },
            { fraction: 1/8, label: "1/8", vulgar: "⅛" },
            { fraction: 1/6, label: "1/6", vulgar: "⅙" },
            { fraction: 1/4, label: "1/4", vulgar: "¼" },
            { fraction: 1/3, label: "1/3", vulgar: "⅓" },
            { fraction: 1/2, label: "1/2", vulgar: "½" },
            { fraction: 5/8, label: "5/8", vulgar: "⅝" },
            { fraction: 2/3, label: "2/3", vulgar: "⅔" },
            { fraction: 3/4, label: "3/4", vulgar: "¾" },
            { fraction: 5/6, label: "5/6", vulgar: "⅚" },
        ];

        const parsed = parseFloat(num);
        if (isNaN(parsed)) return num;

        const fractionTolerance = 0.008; // Tolerance to catch common rounding (e.g., 0.33 → 1/3, 0.66 → 2/3)
        const wholeTolerance = 0.02; // Larger tolerance for whole numbers (e.g., 0.99 → 1, 1.98 → 2)
        const whole = Math.floor(parsed);
        const decimal = parsed - whole;

        // Check if it's basically a whole number (including cases like 0.99 or 1.01)
        if (decimal < wholeTolerance || decimal > (1 - wholeTolerance)) {
            return `${decimal > (1 - wholeTolerance) ? whole + 1 : whole}`;
        }

        // Check for fraction match
        const match = allowedFractions.find(f => Math.abs(decimal - f.fraction) < fractionTolerance);

        if (match) {
            const fractionSpan = <span style={{ fontSize: '1.3em' }}>{match.vulgar}</span>;
            if (whole > 0) {
                return <>{whole} {fractionSpan}</>;
            }
            return fractionSpan;
        }

        // Fall back to decimal, cleaned up
        const rounded = parsed.toFixed(3);
        return rounded.replace(/\.00$/, '');
    };

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState(null);
    const [currentImageSrc, setCurrentImageSrc] = useState(`/images/recipeImages/${recipeId}.jpg`);
    const [shouldUseSupabase, setShouldUseSupabase] = useState(false);
    const supabaseImageUrl = useSupabaseRecipeImage(recipeId, shouldUseSupabase);

    // Update currentImageSrc when supabaseImageUrl changes
    useEffect(() => {
        if (shouldUseSupabase) {
            if (supabaseImageUrl) {
                console.log('Using Supabase image for recipe:', recipeId);
                setCurrentImageSrc(supabaseImageUrl);
            } else {
                // Supabase fetch failed or returned null, use placeholder
                console.log('Supabase image failed for recipe:', recipeId, 'using placeholder');
                setCurrentImageSrc('/images/recipeImages/placeholder_recipe.jpg');
            }
        }
    }, [supabaseImageUrl, shouldUseSupabase, recipeId]);

    // First useEffect to get the session token
    useEffect(() => {
        const getSessionToken = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.access_token) {
                    setSessionToken(session.access_token);
                } else {
                    console.error('No access token found in session');
                    setLoadingError('Authentication failed');
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error getting session:', error);
                setLoadingError('Authentication failed');
                setLoading(false);
            }
        };
        getSessionToken();
    }, []);

    // Second useEffect for fetching recipe data
    useEffect(() => {
        const fetchRecipeData = async () => {
            if (!sessionToken) {
                return;
            }

            try {
                // Fetch recipe details
                const recipeResponse = await fetchWithCSRF(`/api/v1/recipe/${recipeId}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!recipeResponse.ok) {
                    throw new Error(`Failed to fetch recipe: ${recipeResponse.status}`);
                }

                const recipeData = await recipeResponse.json();
                
                if (Array.isArray(recipeData) && recipeData.length > 0) {
                    const recipe = recipeData[0];
                    setRecipe(recipe);
                    setRecipeName(recipe.name);
                    
                    // Use URL servings parameter if available, otherwise use recipe's default servings
                    const urlServings = searchParams.get('servings');
                    setServings(urlServings ? parseInt(urlServings) : (recipe.servings || 1));
                    
                    setTiming({
                        prepTime: recipe.prep_time || 0,
                        cookTime: recipe.cook_time || 0,
                        totalTime: (recipe.prep_time || 0) + (recipe.cook_time || 0)
                    });
                    
                    setNutrition({
                        calories: Math.round(parseFloat(recipe.calories) || 0),
                        protein: Math.round(parseFloat(recipe.protein) || 0),
                        carbohydrate: Math.round(parseFloat(recipe.carbohydrate) || 0),
                        fat: Math.round(parseFloat(recipe.fat) || 0)
                    });
                } else {
                    throw new Error('Recipe not found');
                }

                // Fetch recipe steps
                const stepsResponse = await fetchWithCSRF(`/api/v1/recipe-step/${recipeId}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!stepsResponse.ok) {
                    throw new Error(`Failed to fetch steps: ${stepsResponse.status}`);
                }

                const stepsData = await stepsResponse.json();
                if (Array.isArray(stepsData)) {
                    setSteps(stepsData.sort((a, b) => a.step_number - b.step_number));
                } else {
                    console.error('Invalid steps data format:', stepsData);
                    setSteps([]);
                }

                // Fetch recipe ingredients
                const ingredientsResponse = await fetchWithCSRF(`/api/v1/recipe-ingredient/recipe/${recipeId}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (!ingredientsResponse.ok) {
                    throw new Error(`Failed to fetch ingredients: ${ingredientsResponse.status}`);
                }

                const ingredientsData = await ingredientsResponse.json();
                if (Array.isArray(ingredientsData)) {
                    setIngredients(ingredientsData);
                } else {
                    console.error('Invalid ingredients data format:', ingredientsData);
                    setIngredients([]);
                }
            } catch (error) {
                console.error('Error fetching recipe data:', error);
                setLoadingError(error.message || 'Failed to load recipe data');
            } finally {
                setLoading(false);
            }
        };

        fetchRecipeData();

        // Cleanup function to clear sessionStorage when component unmounts
        return () => {
            sessionStorage.removeItem('currentMealId');
        };
    }, [recipeId, searchParams, sessionToken]);

    // Check if recipe is disliked when recipe data is loaded
    useEffect(() => {
        const checkDislikeStatus = async () => {
            if (recipeId && sessionToken) {
                try {
                    const status = await getRecipeDislikeStatus(recipeId);
                    setIsDisliked(status);
                } catch (error) {
                    console.error('Error checking dislike status:', error);
                }
            }
        };
        checkDislikeStatus();
    }, [recipeId, sessionToken]);

    // Handle toggling dislike status
    const handleToggleDislike = async () => {
        if (!recipeId || isTogglingDislike) return;
        
        setIsTogglingDislike(true);
        try {
            if (isDisliked) {
                const success = await deleteRecipeDislike(recipeId);
                if (success) {
                    setIsDisliked(false);
                }
            } else {
                const success = await addRecipeDislike(recipeId);
                if (success) {
                    setIsDisliked(true);
                }
            }
        } catch (error) {
            console.error('Error toggling dislike:', error);
        } finally {
            setIsTogglingDislike(false);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedPlan('');
        setServings(1);
        setMealType('');
        setDateError(''); // Clear error when closing modal
        setMealTypeError(''); // Clear error when closing modal
    };

    const handleModalShow = () => {
        setShowModal(true);
    };

    const handleAddToMealPlan = async (e) => {
        e.preventDefault();

        // Validate that a date option is selected
        if (!selectedPlan) {
            setDateError('Please select when you want to cook this meal');
            return;
        }

        // Validate that a meal type is selected
        // if (!mealType) {
        //     setMealTypeError('Please select which meal of the day it will be cooked');
        //     return;
        // }

        // Clear any previous errors
        setDateError('');
        setMealTypeError('');

        try {
            const mealData = {
                recipe_id: recipeId,
                app_user_id: pgUser.id,
                serving_size: servings,
                meal_of_day: 'dinner',
                planned_cook_date: selectedPlannedCookedDate,
                actual_cook_date: null,
                has_been_cooked: false,
                is_favorite: false
            };
            console.log("about to send mealData: ", mealData);
            // console.log('Meal Data:', mealData);
            const result = await addMealToMealPlanByDate(mealData);
            
            // Check if the result indicates success
            if (result) {
                handleModalClose();
            } else {
                // Handle case where backend returns false or error
                console.error('Erro to add meal to plan');
            }
        } catch (error) {
            console.error('Error adding recipe to meal plan:', error);
            // The error state is already set by the hook, so we don't need to set it here
        }
    };

    if (loading) {
        return (
            <div className="container text-center my-4">
                <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                    <span className="visually-hidden">Loading recipe...</span>
                </Spinner>
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container">
                <div className="alert alert-warning" role="alert">
                    <p className="mb-0">{loadingError}</p>
                    <button 
                        className="btn btn-sm btn-outline-warning mt-2"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!recipe) {
        return (
            <div className="container">
                <div className="alert alert-info" role="alert">
                    <p className="mb-0">Recipe not found.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0">
            <div className="position-relative mb-5">
                <div 
                    style={{ 
                        width: '100%',
                        height: '60vh',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <img 
                        src={currentImageSrc}
                        onError={(e) => {
                            if (currentImageSrc.includes('/images/recipeImages/') && !shouldUseSupabase) {
                                // Local image failed, try Supabase
                                console.log('Local image failed for recipe:', recipeId, 'trying Supabase');
                                setShouldUseSupabase(true);
                            } else if (currentImageSrc !== '/images/recipeImages/placeholder_recipe.jpg') {
                                // Both local and Supabase failed (or Supabase not available), use placeholder
                                console.log('Using placeholder for recipe:', recipeId);
                                e.target.onerror = null; // Prevent infinite loop
                                setCurrentImageSrc('/images/recipeImages/placeholder_recipe.jpg');
                            }
                        }}
                        alt={recipeName}
                        style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                        }}
                    />
                    <div 
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            // background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 100%)'
                        }}
                    />
                    <div 
                        style={{
                            position: 'absolute',
                            bottom: '2rem',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            textAlign: 'center',
                            width: '100%',
                            padding: '0 1rem'
                        }}
                    >
                        <h1 
                            style={{
                                fontSize: '2.5rem',
                                fontWeight: '600',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                marginBottom: '1rem'
                            }}
                        >
                            {recipeName}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="container px-4">
                <div className="d-flex flex-column align-items-center mb-4">
                    {pgUser && pgUser.setup_complete && pgUser.has_access && (
                        <div className="d-flex flex-column align-items-center gap-3">
                            <div className="d-flex gap-3">
                                <button 
                                    onClick={handleModalShow}
                                    style={{ 
                                        backgroundColor: '#52cd92',
                                        borderColor: 'transparent',
                                        borderRadius: '4px',
                                        fontSize: '1rem',
                                        marginBottom: '2rem',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        transition: 'transform 0.2s, background-color 0.2s'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#45b07c'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#52cd92'}
                                >
                                    Add to Meal Plan
                                </button>
                                <CompleteRecipeCookingButton
                                    recipeId={recipeId}
                                    appUserId={pgUser.id}
                                    servingSize={servings}
                                />
                                <button
                                    className="btn d-flex align-items-center gap-2"
                                    style={{
                                        padding: '7px 16px',
                                        fontSize: '1rem',
                                        borderRadius: '4px',
                                        backgroundColor: isDisliked ? '#dc3545' : '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        transition: 'background-color 0.2s',
                                        marginBottom: '2rem'
                                    }}
                                    onClick={handleToggleDislike}
                                    disabled={isTogglingDislike}
                                    title={isDisliked ? "Remove from dislikes" : "Dislike this recipe"}
                                >
                                    <img 
                                        src={getIconUrl(isDisliked ? 'thumbDown-filled.svg' : 'thumbDown.svg')} 
                                        alt="Dislike" 
                                        style={{ 
                                            width: '20px', 
                                            height: '20px',
                                            filter: 'brightness(0) invert(1)' // Make icon white to match button text
                                        }}
                                    />
                                    {isDisliked ? 'Disliked' : 'Dislike'}
                                </button>
                            </div>

                            <EditRecipeButton recipeId={recipeId} />
                        </div>
                    )}

                    {(!pgUser || !pgUser.setup_complete) && (
                        <div className="alert alert-warning">
                            <p className="h5 mb-3">Complete meal preferences to start adding recipes to your meal plan!</p>
                            <Button onClick={() => navigate('/setup')}>Complete Setup</Button>
                        </div>
                    )}

                    {pgUser && pgUser.setup_complete && !pgUser.has_access && (
                        <div className="alert alert-warning">
                            <p className="h5 mb-3">Finish your account setup to start adding recipes to your meal plan!</p>
                            <Button onClick={() => navigate('/pricing')}>Complete Setup</Button>
                        </div>
                    )}

                    <div className="d-flex flex-column align-items-center mb-4 w-100">
                        <div className="mb-3">
                            <ul className="list-inline mb-0">
                                <li className="list-inline-item me-4">
                                    <span className="text-muted">Prep:</span> {timing.prepTime} min
                                </li>
                                <li className="list-inline-item">
                                    <span className="text-muted">Cook:</span> {timing.cookTime} min
                                </li>
                            </ul>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.calories}</div>
                                <div className="text-muted small">calories</div>
                            </div>
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.protein}g</div>
                                <div className="text-muted small">protein</div>
                            </div>
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.carbohydrate}g</div>
                                <div className="text-muted small">carbs</div>
                            </div>
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.fat}g</div>
                                <div className="text-muted small">fat</div>
                            </div>
                        </div>
                        <div className="mt-1">
                            <small className="text-muted">per serving</small>
                        </div>
                        <div className="d-flex align-items-center justify-content-center mt-4 gap-3">
                            <button 
                                className="btn p-0" 
                                onClick={() => setServings(prev => Math.max(1, prev - 1))}
                                style={{ 
                                    border: 'none', 
                                    background: 'none',
                                    color: '#52cd92',
                                    fontSize: '1.5rem',
                                    lineHeight: 1,
                                    fontWeight: 'bold'
                                }}
                            >
                                −
                            </button>
                            <div className="d-flex flex-column align-items-center">
                                <span className="h4 mb-0">{servings}</span>
                                <small className="text-muted">{servings === 1 ? 'serving' : 'servings'}</small>
                            </div>
                            <button 
                                className="btn p-0" 
                                onClick={() => setServings(prev => Math.min(16, prev + 1))}
                                style={{ 
                                    border: 'none', 
                                    background: 'none',
                                    color: '#52cd92',
                                    fontSize: '1.5rem',
                                    lineHeight: 1,
                                    fontWeight: 'bold'
                                }}
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-4 w-100" style={{ maxWidth: '1200px' }}>
                    <div className="col-12 col-lg-4 mb-1">
                        <div>
                            <div className="card-header rounded-top">
                                <h3 className="h5 mb-0">Ingredients</h3>
                            </div>
                            <div className="pt-3">
                                <div className="d-flex flex-column gap-2">
                                    {ingredients.map((ingredient, index) => (
                                        
                                        <div key={index} className="p-2 border rounded d-flex align-items-center">
                                            <div 
                                                style={{ 
                                                    width: '50px',
                                                    height: '50px',
                                                    marginRight: '1rem',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <img 
                                                    src={getIngredientImageUrl(ingredient.ingredient_id)}
                                                    alt={ingredient.ingredient_name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '4px',
                                                        // backgroundColor: '#f0f0f0'
                                                    }}
                                                    onError={(e) => {
                                                        if (e.target.getAttribute('data-error-handled')) return;
                                                        e.target.setAttribute('data-error-handled', 'true');
                                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMjAgMTVIMzBMMzIuNSAxNy41SDM3LjVDMzguODgxIDE3LjUgNDAgMTguNjE5IDQwIDIwVjM1QzQwIDM2LjM4MSAzOC44ODEgMzcuNSAzNy41IDM3LjVIMTIuNUMxMS4xMTkgMzcuNSAxMCAzNi4zODEgMTAgMzVWMjBDMTAgMTguNjE5IDExLjExOSAxNy41IDEyLjUgMTcuNUgxNy41TDIwIDE1Wk0yNSAzNUMyOC44NjYgMzUgMzIgMzEuODY2IDMyIDI4QzMyIDI0LjEzNCAyOC44NjYgMjEgMjUgMjFDMjEuMTM0IDIxIDE4IDI0LjEzNCAxOCAyOEMxOCAzMS44NjYgMjEuMTM0IDM1IDI1IDM1Wk0yNSAzMi41QzIyLjUxNSAzMi41IDIwLjUgMzAuNDg1IDIwLjUgMjhDMjAuNSAyNS41MTUgMjIuNTE1IDIzLjUgMjUgMjMuNUMyNy40ODUgMjMuNSAyOS41IDI1LjUxNSAyOS41IDI4QzI5LjUgMzAuNDg1IDI3LjQ4NSAzMi41IDI1IDMyLjVaIiBmaWxsPSIjOTA5MDkwIi8+PC9zdmc+';
                                                    }}
                                                />
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center">
                                                    <span className="fw-bold me-2">
                                                        {formatQuantity(ingredient.quantity * (servings / (recipe?.servings || 1)))} {ingredient.unit_of_measurement}
                                                    </span>
                                                    <span>{ingredient.ingredient_name}</span>
                                                </div>
                                                {ingredient.notes && (
                                                    <small className="text-muted d-block mt-1">{ingredient.notes}</small>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-8">
                        <div>
                            <div className="card-header rounded-top">
                                <h3 className="h5 mb-0">Recipe Steps</h3>
                            </div>
                            <div className="pt-3">
                                <div className="d-flex flex-column gap-3">
                                    {steps.map((step) => (
                                        <div key={step.id} className="card">
                                            <div className="card-body d-flex gap-3">
                                                <div>
                                                    <span className="badge rounded-pill fs-6" style={{ backgroundColor: '#52cd92' }}>
                                                        {step.step_number}
                                                    </span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    {step.instruction}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Add {recipeName} to Meal Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="add-to-meal-plan-modal">
                        <Form.Group className="mb-3">
                            <Form.Label>Add to</Form.Label>
                            <div className="d-flex gap-2">
                                <Button 
                                    variant={selectedPlan === 'this-week' ? 'primary' : 'outline-primary'}
                                    onClick={() => {
                                        setSelectedPlan('this-week');
                                        setDateError(''); // Clear error when option is selected
                                        setSelectedPlannedCookedDate(getTodayLocalDateString());
                                    }}
                                    className={`custom-modal-button-option ${selectedPlan === 'this-week' ? 'selected' : ''}`}
                                >
                                    This week
                                </Button>
                                <Button 
                                    variant={selectedPlan === 'next-week' ? 'primary' : 'outline-primary'}
                                    onClick={() => {
                                        setSelectedPlan('next-week');
                                        setDateError(''); // Clear error when option is selected
                                        setSelectedPlannedCookedDate(getDateWithOffset(7));
                                        console.log("selected planned cooked date0: ", getDateWithOffset(7));
                                        console.log("getTodayLocalDateString0: ", getTodayLocalDateString());
                                        console.log("getDateWithOffset(7):0 ", getDateWithOffset(7));
                                    }}
                                    className={`custom-modal-button-option ${selectedPlan === 'next-week' ? 'selected' : ''}`}
                                >
                                    Next week
                                </Button>
                                <>

                                    
                                    {showCalendar && selectedPlan === 'other' && (
                                        <Form.Control
                                            type="date"
                                            style={{
                                                backgroundColor: '#fff',
                                                border: '1px solid #ced4da',
                                                borderRadius: '8px',
                                                width: '30%'
                                            }}
                                            onChange={(e) => {
                                                setSelectedPlannedCookedDate(e.target.value)
                                                console.log("selected planned cooked date: ", e.target.value);
                                                console.log("getTodayLocalDateString: ", getTodayLocalDateString());
                                                console.log("getDateWithOffset(7): ", getDateWithOffset(7));
                                            }}
                                            min={getTodayLocalDateString()}
                                        />
                                    )}
                                </>
                            </div>
                            {dateError && (
                                <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                                    {dateError}
                                </div>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Servings</Form.Label>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant=""
                                    size="md"
                                    onClick={() => {
                                        if (servings > 1) {
                                            setServings(servings - 1);
                                        }
                                    }}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    <img src="/icons/minus-circle-red.svg" alt="Add Breakfast Meal" />
                                </Button>
                                <Form.Control 
                                    type="number" 
                                    min="1"
                                    max="16"
                                    value={servings}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        if (value > 16) {
                                            setServings(16);
                                        } else if (value < 1) {
                                            setServings(1);
                                        } else {
                                            setServings(value);
                                        }
                                    }}
                                    style={{
                                        textAlign: 'center',
                                        width: '80px'
                                    }}
                                    className="form-control-custom"
                                />
                                <Button
                                    variant=""
                                    size="md"
                                    onClick={() => {
                                        if (servings < 16) {
                                            setServings(servings + 1);
                                        }
                                    }}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    <img src="/icons/plus-circle-green.svg" alt="Add Breakfast Meal" />
                                </Button>
                            </div>
                        </Form.Group>

                        {/* <Form.Group className="mb-3">
                            <Form.Label>Which Meal of the Day?</Form.Label>
                            <div className="d-flex gap-2 flex-wrap">
                                <Button 
                                    variant={mealType === 'breakfast' ? 'primary' : 'outline-primary'}
                                    onClick={() => {
                                        setMealType('breakfast');
                                        setMealTypeError(''); // Clear error when option is selected
                                    }}
                                    className={`custom-modal-button-option ${mealType === 'breakfast' ? 'selected' : ''}`}
                                >
                                    Breakfast
                                </Button>
                                <Button 
                                    variant={mealType === 'lunch' ? 'primary' : 'outline-primary'}
                                    onClick={() => {
                                        setMealType('lunch');
                                        setMealTypeError(''); // Clear error when option is selected
                                    }}
                                    className={`custom-modal-button-option ${mealType === 'lunch' ? 'selected' : ''}`}
                                >
                                    Lunch
                                </Button>
                                <Button 
                                    variant={mealType === 'dinner' ? 'primary' : 'outline-primary'}
                                    onClick={() => {
                                        setMealType('dinner');
                                        setMealTypeError(''); // Clear error when option is selected
                                    }}
                                    className={`custom-modal-button-option ${mealType === 'dinner' ? 'selected' : ''}`}
                                >
                                    Dinner
                                </Button>
                            </div>
                            {mealTypeError && (
                                <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                                    {mealTypeError}
                                </div>
                            )}
                        </Form.Group> */}
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddToMealPlan}>
                        Add to Meal Plan
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default RecipeSteps;